import React, { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setSelectedCategory } from '../../redux/slice/game.slice';
import { fetchAllCategories } from '../../redux/slice/category.slice';
import { Swords, Compass, Flame, ShieldAlert, Award, Radio, PlayCircle, Star, MousePointerClick, Crosshair, Gamepad2, Brain, GraduationCap, Trophy, Cpu, Sparkles } from 'lucide-react';
import gsap from 'gsap';

// Curated aesthetic category mappings with neon glows, orbital colors, and icon styles
const CATEGORIES_STYLE_MAPPING = {
  Action: {
    icon: Swords,
    color: "from-cyber-pink to-pink-500",
    glowColor: "#ff007a",
    shadowColor: "rgba(255, 0, 122, 0.6)",
    borderColor: "border-cyber-pink/40 group-hover:border-cyber-pink",
    iconColor: "text-cyber-pink",
  },
  Racing: {
    icon: Radio,
    color: "from-cyber-blue to-blue-500",
    glowColor: "#0066ff",
    shadowColor: "rgba(0, 102, 255, 0.6)",
    borderColor: "border-cyber-blue/40 group-hover:border-cyber-blue",
    iconColor: "text-cyber-blue",
  },
  Horror: {
    icon: ShieldAlert,
    color: "from-purple-900 to-cyber-purple",
    glowColor: "#bd00ff",
    shadowColor: "rgba(189, 0, 255, 0.6)",
    borderColor: "border-cyber-purple/40 group-hover:border-cyber-purple",
    iconColor: "text-cyber-purple",
  },
  RPG: {
    icon: Award,
    color: "from-amber-500 to-cyber-green",
    glowColor: "#00ff66",
    shadowColor: "rgba(0, 255, 102, 0.6)",
    borderColor: "border-cyber-green/40 group-hover:border-cyber-green",
    iconColor: "text-cyber-green",
  },
  "Open World": {
    icon: Compass,
    color: "from-emerald-500 to-cyber-cyan",
    glowColor: "#00f0ff",
    shadowColor: "rgba(0, 240, 255, 0.6)",
    borderColor: "border-cyber-cyan/40 group-hover:border-cyber-cyan",
    iconColor: "text-cyber-cyan",
  },
  VR: {
    icon: PlayCircle,
    color: "from-cyan-400 to-cyber-blue",
    glowColor: "#00c8ff",
    shadowColor: "rgba(0, 200, 255, 0.6)",
    borderColor: "border-cyber-blue/40 group-hover:border-cyber-blue",
    iconColor: "text-cyber-cyan",
  },
  Multiplayer: {
    icon: Star,
    color: "from-cyber-cyan to-cyber-purple",
    glowColor: "#a855f7",
    shadowColor: "rgba(168, 85, 247, 0.6)",
    borderColor: "border-cyber-purple/40 group-hover:border-cyber-purple",
    iconColor: "text-cyber-purple",
  },
  Adventure: {
    icon: Flame,
    color: "from-cyber-pink to-cyber-purple",
    glowColor: "#ff007a",
    shadowColor: "rgba(255, 0, 122, 0.6)",
    borderColor: "border-cyber-pink/40 group-hover:border-cyber-pink",
    iconColor: "text-cyber-pink",
  },
  "Clicker / Idle": {
    icon: MousePointerClick,
    color: "from-cyber-cyan to-cyber-blue",
    glowColor: "#00f0ff",
    shadowColor: "rgba(0, 240, 255, 0.6)",
    borderColor: "border-cyber-cyan/40 group-hover:border-cyber-cyan",
    iconColor: "text-cyber-cyan",
  },
  Clicker: {
    icon: MousePointerClick,
    color: "from-cyber-cyan to-cyber-blue",
    glowColor: "#00f0ff",
    shadowColor: "rgba(0, 240, 255, 0.6)",
    borderColor: "border-cyber-cyan/40 group-hover:border-cyber-cyan",
    iconColor: "text-cyber-cyan",
  },
  Idle: {
    icon: MousePointerClick,
    color: "from-cyber-cyan to-cyber-blue",
    glowColor: "#00f0ff",
    shadowColor: "rgba(0, 240, 255, 0.6)",
    borderColor: "border-cyber-cyan/40 group-hover:border-cyber-cyan",
    iconColor: "text-cyber-cyan",
  },
  Shooter: {
    icon: Crosshair,
    color: "from-cyber-blue to-blue-500",
    glowColor: "#0066ff",
    shadowColor: "rgba(0, 102, 255, 0.6)",
    borderColor: "border-cyber-blue/40 group-hover:border-cyber-blue",
    iconColor: "text-cyber-blue",
  },
  "Battle Royale": {
    icon: Swords,
    color: "from-cyber-pink to-pink-500",
    glowColor: "#ff007a",
    shadowColor: "rgba(255, 0, 122, 0.6)",
    borderColor: "border-cyber-pink/40 group-hover:border-cyber-pink",
    iconColor: "text-cyber-pink",
  },
  Arcade: {
    icon: Gamepad2,
    color: "from-purple-900 to-cyber-purple",
    glowColor: "#bd00ff",
    shadowColor: "rgba(189, 0, 255, 0.6)",
    borderColor: "border-cyber-purple/40 group-hover:border-cyber-purple",
    iconColor: "text-cyber-purple",
  },
  "Brain Training": {
    icon: Brain,
    color: "from-emerald-500 to-cyber-green",
    glowColor: "#00ff66",
    shadowColor: "rgba(0, 255, 102, 0.6)",
    borderColor: "border-cyber-green/40 group-hover:border-cyber-green",
    iconColor: "text-cyber-green",
  },
  Educational: {
    icon: GraduationCap,
    color: "from-amber-500 to-cyber-green",
    glowColor: "#00ff66",
    shadowColor: "rgba(0, 255, 102, 0.6)",
    borderColor: "border-cyber-green/40 group-hover:border-cyber-green",
    iconColor: "text-cyber-green",
  },
  Strategy: {
    icon: Award,
    color: "from-amber-500 to-cyber-green",
    glowColor: "#00ff66",
    shadowColor: "rgba(0, 255, 102, 0.6)",
    borderColor: "border-cyber-green/40 group-hover:border-cyber-green",
    iconColor: "text-cyber-green",
  },
  Sports: {
    icon: Trophy,
    color: "from-cyber-blue to-blue-500",
    glowColor: "#0066ff",
    shadowColor: "rgba(0, 102, 255, 0.6)",
    borderColor: "border-cyber-blue/40 group-hover:border-cyber-blue",
    iconColor: "text-cyber-blue",
  },
  Simulation: {
    icon: Cpu,
    color: "from-cyan-400 to-cyber-blue",
    glowColor: "#00c8ff",
    shadowColor: "rgba(0, 200, 255, 0.6)",
    borderColor: "border-cyber-blue/40 group-hover:border-cyber-blue",
    iconColor: "text-cyber-cyan",
  },
  Casual: {
    icon: Sparkles,
    color: "from-cyber-pink to-cyber-purple",
    glowColor: "#ff007a",
    shadowColor: "rgba(255, 0, 122, 0.6)",
    borderColor: "border-cyber-pink/40 group-hover:border-cyber-pink",
    iconColor: "text-cyber-pink",
  },
  Indie: {
    icon: Sparkles,
    color: "from-cyber-purple to-purple-500",
    glowColor: "#bd00ff",
    shadowColor: "rgba(189, 0, 255, 0.6)",
    borderColor: "border-cyber-purple/40 group-hover:border-cyber-purple",
    iconColor: "text-cyber-purple",
  },
};

// Rich default list used as visual mock fallbacks if backend is unpopulated
const MOCK_CATEGORIES_DATA = [
  { name: "Action", icon: Swords, color: "from-cyber-pink to-pink-500" },
  { name: "Racing", icon: Radio, color: "from-cyber-blue to-blue-500" },
  { name: "Horror", icon: ShieldAlert, color: "from-purple-900 to-cyber-purple" },
  { name: "RPG", icon: Award, color: "from-amber-500 to-cyber-green" },
  { name: "Open World", icon: Compass, color: "from-emerald-500 to-cyber-cyan" },
  { name: "VR", icon: PlayCircle, color: "from-cyan-400 to-cyber-blue" },
  { name: "Multiplayer", icon: Star, color: "from-cyber-cyan to-cyber-purple" },
  { name: "Adventure", icon: Flame, color: "from-cyber-pink to-cyber-purple" },
  { name: "Clicker / Idle", icon: MousePointerClick, color: "from-cyber-cyan to-cyber-blue" },
  { name: "Shooter", icon: Crosshair, color: "from-cyber-blue to-blue-500" },
];

const getCategoryStyle = (categoryName = '', customHexColor = '') => {
  const normalized = categoryName.trim();
  const matchedKey = Object.keys(CATEGORIES_STYLE_MAPPING).find(
    k => k.toLowerCase() === normalized.toLowerCase()
  );

  if (matchedKey) return CATEGORIES_STYLE_MAPPING[matchedKey];

  if (customHexColor) {
    return {
      icon: Swords,
      color: `from-[${customHexColor}] to-black`,
      glowColor: customHexColor,
      shadowColor: `${customHexColor}80`,
      borderColor: `border-[${customHexColor}]/40 group-hover:border-[${customHexColor}]`,
      iconColor: `text-[${customHexColor}]`,
    };
  }

  const fallbackStyles = [
    { icon: Swords, color: "from-cyber-pink to-pink-500", glowColor: "#ff007a", shadowColor: "rgba(255, 0, 122, 0.6)", borderColor: "border-cyber-pink/40 group-hover:border-cyber-pink", iconColor: "text-cyber-pink" },
    { icon: Compass, color: "from-cyber-cyan to-cyber-blue", glowColor: "#00f0ff", shadowColor: "rgba(0, 240, 255, 0.6)", borderColor: "border-cyber-cyan/40 group-hover:border-cyber-cyan", iconColor: "text-cyber-cyan" },
    { icon: Flame, color: "from-cyber-purple to-purple-500", glowColor: "#bd00ff", shadowColor: "rgba(189, 0, 255, 0.6)", borderColor: "border-cyber-purple/40 group-hover:border-cyber-purple", iconColor: "text-cyber-purple" },
    { icon: Award, color: "from-cyber-green to-green-500", glowColor: "#00ff66", shadowColor: "rgba(0, 255, 102, 0.6)", borderColor: "border-cyber-green/40 group-hover:border-cyber-green", iconColor: "text-cyber-green" }
  ];

  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = normalized.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % fallbackStyles.length;
  return fallbackStyles[index];
};

export function CategoriesSkeleton() {
  return (
    <div className="flex flex-row items-center justify-center gap-8 sm:gap-10 md:gap-12 px-6 animate-pulse">
      {[...Array(8)].map((_, idx) => (
        <div
          key={idx}
          className={`flex flex-col items-center flex-shrink-0 py-2 px-1 ${
            idx >= 6 ? 'hidden xl:flex' : idx >= 4 ? 'hidden lg:flex' : idx >= 3 ? 'hidden md:flex' : 'flex'
          }`}
        >
          <div className="relative flex items-center justify-center">
            <div className="w-20 h-20 sm:w-22 sm:h-22 md:w-24 md:h-24 rounded-full p-[2px] bg-white/10 flex items-center justify-center border border-white/15">
              <div className="w-full h-full rounded-full bg-[#10172e]/90 flex items-center justify-center border border-white/10">
                <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-white/10" />
              </div>
            </div>
          </div>
          <div className="h-3.5 w-16 bg-white/10 rounded mt-4" />
        </div>
      ))}
    </div>
  );
}

export default function Categories() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { categories, loading: categoryLoading } = useSelector((state) => state.category);
  const { loading: homeLoading } = useSelector((state) => state.home || {});
  const { selectedCategory } = useSelector((s) => s.game);

  const trackRef = useRef(null);
  const tweenRef = useRef(null);

  // Fetch active categories on mount
  useEffect(() => {
    dispatch(fetchAllCategories());
  }, [dispatch]);

  // Compute active category list (DB list filtered by isActive, fall back to default list)
  const activeCategories = categories && categories.length > 0
    ? categories.filter(c => c.isActive !== false)
    : MOCK_CATEGORIES_DATA;

  // Triplicate so the loop is seamless even on extremely wide layouts
  const loopedCategories = [...activeCategories, ...activeCategories, ...activeCategories];

  /* ── Infinite marquee via GSAP ── */
  useEffect(() => {
    let animationFrameId;
    let timerId;

    const initMarquee = () => {
      const track = trackRef.current;
      if (!track) return;

      const oneSetWidth = track.scrollWidth / 3;
      if (!oneSetWidth || oneSetWidth <= 0) return;

      if (tweenRef.current) {
        tweenRef.current.kill();
      }

      gsap.set(track, { x: 0 });

      tweenRef.current = gsap.to(track, {
        x: `-=${oneSetWidth}`,
        duration: 35,
        ease: 'none',
        repeat: -1,
        modifiers: {
          x: gsap.utils.unitize((x) => parseFloat(x) % oneSetWidth),
        },
      });
    };

    // Defer initialization to ensure DOM element is mounted and layout dimensions are fully calculated
    timerId = setTimeout(() => {
      animationFrameId = requestAnimationFrame(initMarquee);
    }, 150);

    return () => {
      clearTimeout(timerId);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (tweenRef.current) tweenRef.current.kill();
    };
  }, [categories, categoryLoading, homeLoading]);

  const pauseScroll = () => { if (tweenRef.current) tweenRef.current.pause(); };
  const resumeScroll = () => { if (tweenRef.current) tweenRef.current.resume(); };

  const handleCategoryClick = (categoryName) => {
    const nextCategory = selectedCategory === categoryName ? 'All' : categoryName;
    dispatch(setSelectedCategory(nextCategory));
    navigate(`/all-games?category=${encodeURIComponent(nextCategory)}`);
  };

  const isSvg = (url = '') => {
    if (!url || typeof url !== 'string') return false;
    if (url.startsWith('blob:')) return true;
    if (url.startsWith('data:image/svg+xml')) return true;
    return url.toLowerCase().split('?')[0].endsWith('.svg');
  };

  return (
    <section
      id="categories-section"
      className="py-8 sm:py-12 relative z-10 select-none overflow-hidden"
    >
      {/* Dynamic Cyber Grid & Multilayer Ambient Halos */}
      <div className="absolute inset-0 bg-cyber-grid opacity-[0.035] pointer-events-none" />
      <div className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[80vw] max-w-[950px] h-[380px] bg-[#00f0ff]/8 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute top-[35%] right-[-8%] w-[420px] h-[420px] bg-cyber-purple/8 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute bottom-[5%] left-[-8%] w-[420px] h-[420px] bg-cyber-blue/8 rounded-full blur-[160px] pointer-events-none" />

      {/* ── Section Header (Standardized with FAQ & Legal Pages) ── */}
      <div className="px-4 sm:px-6 lg:px-8 mb-6 sm:mb-8 relative z-10">
        <div className="max-w-4xl mx-auto flex flex-col items-center justify-center text-center">
          <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2 justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
            Curated Catalog
          </p>
          <h2 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-normal break-words">
            Explore <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">Categories</span>
          </h2>
          <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full mx-auto"></div>
          <p className="text-gray-400 font-sans max-w-lg text-xs sm:text-sm mx-auto mt-3.5 leading-relaxed">
            Browse our game collections. Click any portal to filter the store and discover verified digital titles.
          </p>
        </div>
      </div>

      {/* ── Full-width Circular Neon Portals Strip ── */}
      <div className="relative w-full overflow-hidden py-4 sm:py-6">
        {/* Edge Fade Masks for Cinematic Blend - minimal below 1024px (lg) */}
        <div className="pointer-events-none absolute left-0 top-0 h-full w-4 sm:w-8 md:w-12 lg:w-44 z-20 bg-gradient-to-r from-[#030308]/30 lg:from-[#030308] via-transparent lg:via-[#030308]/90 to-transparent"></div>
        <div className="pointer-events-none absolute right-0 top-0 h-full w-4 sm:w-8 md:w-12 lg:w-44 z-20 bg-gradient-to-l from-[#030308]/30 lg:from-[#030308] via-transparent lg:via-[#030308]/90 to-transparent"></div>

        {categoryLoading || homeLoading || !categories || categories.length === 0 ? (
          <CategoriesSkeleton />
        ) : (
          /* Moving Track */
          <div
            ref={trackRef}
            onMouseEnter={pauseScroll}
            onMouseLeave={resumeScroll}
            className="flex flex-row items-center gap-8 sm:gap-10 md:gap-12 px-6 will-change-transform py-4 sm:py-5"
            style={{ width: 'max-content' }}
          >
          {loopedCategories.map((cat, idx) => {
            const style = getCategoryStyle(cat.name, cat.color);
            const IconComponent = style.icon;
            const isCurrent = selectedCategory === cat.name;
            const glowColor = cat.color || style.glowColor || '#00f0ff';

            return (
              <div
                key={`${cat.name}-${idx}`}
                onClick={() => handleCategoryClick(cat.name)}
                className="cursor-pointer flex flex-col items-center group relative flex-shrink-0 py-2 px-1"
              >
                {/* Visual container that translates smoothly on hover without losing cursor contact */}
                <div className="flex flex-col items-center transition-transform duration-300 group-hover:-translate-y-2 pointer-events-none">
                  {/* Circular Neon Portal Construct */}
                  <div className="relative flex items-center justify-center portal-ring transition-transform duration-300 group-hover:scale-105">
                    {/* Ambient Radial Bloom (Subdued below 1024px) */}
                    <div
                      className="absolute -inset-2.5 lg:-inset-4 rounded-full opacity-0 group-hover:opacity-60 lg:group-hover:opacity-100 blur-md transition-opacity duration-500 pointer-events-none -z-10"
                      style={{ background: `radial-gradient(circle, ${glowColor}88 0%, transparent 70%)` }}
                    />
                    {/* Outer Orbiting Particle Sparks */}
                    <div
                      className={`absolute -inset-2.5 sm:-inset-3 rounded-full border border-dashed transition-all duration-500 pointer-events-none ${isCurrent
                        ? 'border-cyber-cyan animate-spin-slow opacity-100 shadow-[0_0_10px_rgba(0,240,255,0.3)] lg:shadow-[0_0_16px_rgba(0,240,255,0.4)]'
                        : 'border-white/15 opacity-40 group-hover:opacity-100 group-hover:animate-spin-slow'
                        }`}
                      style={{ borderColor: isCurrent ? '#00f0ff' : `${glowColor}60` }}
                    />

                    {/* Intermediate Glowing Concentric Ring */}
                    <div
                      className={`w-20 h-20 sm:w-22 sm:h-22 md:w-24 md:h-24 rounded-full p-[2px] transition-all duration-500 relative flex items-center justify-center ${isCurrent
                        ? 'bg-gradient-to-tr from-cyber-cyan via-[#5c60ff] to-cyber-purple shadow-[0_0_14px_rgba(0,240,255,0.35)] lg:shadow-[0_0_28px_rgba(0,240,255,0.5)]'
                        : 'bg-gradient-to-b from-white/20 via-white/5 to-white/10 group-hover:bg-gradient-to-tr group-hover:from-cyber-cyan group-hover:via-[#5c60ff] group-hover:to-cyber-purple group-hover:shadow-[0_0_14px_rgba(0,240,255,0.25)] lg:group-hover:shadow-[0_0_24px_rgba(0,240,255,0.35)]'
                        }`}
                    >
                      {/* Inner Glass Vortex Chamber */}
                      <div className="w-full h-full rounded-full bg-gradient-to-b from-[#10172e]/95 via-[#080d1e]/98 to-[#03050c] backdrop-blur-2xl flex items-center justify-center relative overflow-hidden border border-white/10 group-hover:border-cyber-cyan/50 transition-colors">
                        {/* Radial Glow Spotlight in Core */}
                        <div
                          className="absolute inset-0 opacity-20 group-hover:opacity-50 transition-opacity duration-500 pointer-events-none"
                          style={{ background: `radial-gradient(circle at center, ${glowColor} 0%, transparent 70%)` }}
                        />

                        {/* Diagonal Holographic Sheen Sweep */}
                        <div className="absolute inset-0 -left-[120%] w-full h-full bg-gradient-to-r from-transparent via-white/[0.18] to-transparent transition-all duration-700 group-hover:left-[120%] pointer-events-none" />

                        {/* Centered Floating Holographic Icon */}
                        <div className="relative z-10 flex items-center justify-center transition-transform duration-300">
                          {cat.icon ? (
                            isSvg(cat.icon) ? (
                              <div
                                className="category-icon w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                                style={{
                                  backgroundColor: isCurrent ? '#00f0ff' : (cat.color || '#ffffff'),
                                  WebkitMaskImage: `url(${cat.icon})`,
                                  maskImage: `url(${cat.icon})`,
                                  WebkitMaskPosition: 'center',
                                  maskPosition: 'center',
                                  WebkitMaskRepeat: 'no-repeat',
                                  maskRepeat: 'no-repeat',
                                  WebkitMaskSize: 'contain',
                                  maskSize: 'contain',
                                }}
                              />
                            ) : (
                              <img
                                src={cat.icon}
                                alt={cat.name}
                                className="category-icon w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                              />
                            )
                          ) : (
                            <IconComponent
                              className={`category-icon w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${isCurrent
                                ? 'text-cyber-cyan drop-shadow-[0_0_8px_rgba(0,240,255,0.6)] lg:drop-shadow-[0_0_14px_rgba(0,240,255,0.9)]'
                                : `${style.iconColor || 'text-gray-300'} group-hover:text-white group-hover:drop-shadow-[0_0_8px_rgba(0,240,255,0.5)] lg:group-hover:drop-shadow-[0_0_12px_rgba(0,240,255,0.7)]`
                                }`}
                            />
                          )}
                        </div>

                        {/* Active Indicator Pulse Dot (Bottom inside portal) */}
                        {isCurrent && (
                          <span className="absolute bottom-2 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] lg:shadow-[0_0_10px_#00f0ff] animate-pulse" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Portal Title */}
                  <div className="flex flex-col items-center mt-4 sm:mt-5">
                    <span
                      className={`text-xs sm:text-sm font-cyber uppercase tracking-wider font-bold transition-all duration-300 whitespace-nowrap text-center ${isCurrent
                        ? 'text-cyber-cyan drop-shadow-[0_0_8px_rgba(0,240,255,0.5)] lg:drop-shadow-[0_0_14px_rgba(0,240,255,0.75)]'
                        : 'text-gray-300 group-hover:text-white group-hover:drop-shadow-[0_0_6px_rgba(0,240,255,0.35)] lg:group-hover:drop-shadow-[0_0_10px_rgba(0,240,255,0.5)]'
                        }`}
                    >
                      {cat.name ? cat.name.replace(/\s*\([^)]*\)/g, '').trim() : ''}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    </section>
  );
}   